import {
	addAdminExternalTicketComment,
	addMyExternalTicketComment,
	closeExternalTicket,
	createExternalTicket,
	getAdminExternalTicket,
	getAdminExternalTicketAttachments,
	getAdminExternalTicketOptions,
	getAdminExternalTickets,
	getExternalTicketOptions,
	getMyExternalTicket,
	getMyExternalTicketAttachments,
	getMyExternalTickets,
	submitExternalTicket,
	updateAdminExternalTicket,
	updateMyExternalTicket,
	uploadAdminExternalTicketAttachment,
	uploadMyExternalTicketAttachment,
} from '../Services/externalTicket';
import { ExternalTicketAdminDraft, ExternalTicketAdminFilters, ExternalTicketDraft } from '../Types/externalTicket';
import { useBizMutation, useBizQuery } from './useBizQuery';

export const externalTicketKeys = {
	all: ['externalTickets'] as const,
	mine: (search: string, page: number) => ['externalTickets', 'mine', search, page] as const,
	mineOne: (id: number) => ['externalTickets', 'mine', id] as const,
	admin: (search: string, page: number, filters: ExternalTicketAdminFilters) =>
		['externalTickets', 'admin', search, page, filters.projectId, filters.statusId, filters.priorityId] as const,
	adminOne: (id: number) => ['externalTickets', 'admin', id] as const,
	options: ['externalTickets', 'options'] as const,
	adminOptions: ['externalTickets', 'adminOptions'] as const,
	attachments: (admin: boolean, id: number) => ['externalTickets', admin ? 'adminAttachments' : 'mineAttachments', id] as const,
};

export const useMyExternalTickets = (search: string, page: number, pageSize = 20) =>
	useBizQuery(externalTicketKeys.mine(search, page), () => getMyExternalTickets(search, page, pageSize));

export const useMyExternalTicket = (id: number) =>
	useBizQuery(externalTicketKeys.mineOne(id), () => getMyExternalTicket(id), id > 0, 0, 'always');

export const useAdminExternalTickets = (search: string, page: number, filters: ExternalTicketAdminFilters, pageSize = 20) =>
	useBizQuery(externalTicketKeys.admin(search, page, filters), () => getAdminExternalTickets(search, page, pageSize, filters));

export const useAdminExternalTicket = (id: number) =>
	useBizQuery(externalTicketKeys.adminOne(id), () => getAdminExternalTicket(id), id > 0, 0, 'always');

export const useExternalTicketOptions = () =>
	useBizQuery(externalTicketKeys.options, getExternalTicketOptions, true, 300);

export const useAdminExternalTicketOptions = () =>
	useBizQuery(externalTicketKeys.adminOptions, getAdminExternalTicketOptions, true, 300);

export const useExternalTicketList = (admin: boolean, search: string, page: number, filters: ExternalTicketAdminFilters, pageSize = 20) =>
	useBizQuery(
		admin ? externalTicketKeys.admin(search, page, filters) : externalTicketKeys.mine(search, page),
		() => (admin ? getAdminExternalTickets(search, page, pageSize, filters) : getMyExternalTickets(search, page, pageSize))
	);

export const useExternalTicketDetail = (admin: boolean, id: number) =>
	useBizQuery(
		admin ? externalTicketKeys.adminOne(id) : externalTicketKeys.mineOne(id),
		() => (admin ? getAdminExternalTicket(id) : getMyExternalTicket(id)),
		id > 0,
		0,
		'always'
	);

export const useExternalTicketWorkspaceOptions = (admin: boolean) =>
	useBizQuery(
		admin ? externalTicketKeys.adminOptions : externalTicketKeys.options,
		() => (admin ? getAdminExternalTicketOptions() : getExternalTicketOptions()),
		true,
		300
	);

export const useExternalTicketAttachments = (admin: boolean, id: number, enabled: boolean) =>
	useBizQuery(
		externalTicketKeys.attachments(admin, id),
		() => (admin ? getAdminExternalTicketAttachments(id) : getMyExternalTicketAttachments(id)),
		enabled && id > 0,
		0,
		'always'
	);

export const useCreateExternalTicket = (onSuccess: (id: number) => void) =>
	useBizMutation(createExternalTicket, {
		getQueryKeyForInvalidation: () => [externalTicketKeys.all],
		onSuccessFn: (ticket) => {
			if (ticket) onSuccess(ticket.id);
		},
	});

export const useUpdateMyExternalTicket = () =>
	useBizMutation(({ id, draft }: { id: number; draft: ExternalTicketDraft }) => updateMyExternalTicket(id, draft), {
		getQueryKeyForInvalidation: () => [externalTicketKeys.all],
	});

export const useUpdateAdminExternalTicket = () =>
	useBizMutation(({ id, draft }: { id: number; draft: ExternalTicketAdminDraft }) => updateAdminExternalTicket(id, draft), {
		getQueryKeyForInvalidation: () => [externalTicketKeys.all],
	});

export const useAddExternalTicketComment = (admin: boolean) =>
	useBizMutation(
		({ id, text }: { id: number; text: string }) =>
			admin ? addAdminExternalTicketComment(id, text) : addMyExternalTicketComment(id, text),
		{ getQueryKeyForInvalidation: () => [externalTicketKeys.all] }
	);

export const useSubmitExternalTicket = () =>
	useBizMutation(
		({ id, projectId, milestoneId }: { id: number; projectId: number; milestoneId?: number }) =>
			submitExternalTicket(id, projectId, milestoneId),
		{ getQueryKeyForInvalidation: () => [externalTicketKeys.all] }
	);

export const useCloseExternalTicket = () =>
	useBizMutation(({ id }: { id: number }) => closeExternalTicket(id), {
		getQueryKeyForInvalidation: () => [externalTicketKeys.all],
	});

export const useUploadExternalTicketAttachment = (admin: boolean) =>
	useBizMutation(
		({ id, file }: { id: number; file: File }) =>
			admin ? uploadAdminExternalTicketAttachment(id, file) : uploadMyExternalTicketAttachment(id, file),
		{ getQueryKeyForInvalidation: ({ id }) => [externalTicketKeys.attachments(admin, id)] }
	);
